import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import { X, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { useEnquiry, readAttribution } from "@/components/site/EnquiryContext";
import { company } from "@/content/site";
import { useReturnFocus } from "@/components/site/Modals";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;
const PAGES = {
  "/": { label: "Home (/)", category: null },
  "/residential": { label: "Residential (/residential)", category: "Residential" },
  "/commercial": { label: "Commercial (/commercial)", category: "Commercial" },
};
const EMPTY = { full_name: "", contact_method: "email", phone: "", email: "", category: "", company: "", project_location: "", tender_deadline: "", drawings_link: "", comment: "", hp_website: "" };
const SUCCESS = "an agent will contact you shortly, thank you for trusting us";
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/;
const PHONE_RE = /^\+?[0-9\s().-]{7,24}(\s*(x|ext\.?)\s*\d{1,6})?$/i;

const validate = (f) => {
  const e = {};
  if (f.full_name.trim().length < 2) e.full_name = "Please enter your full name.";
  if (!f.category) e.category = "Please choose Residential or Commercial.";
  if (f.contact_method !== "email") {
    const digits = f.phone.split(/x|ext/i)[0].replace(/\D/g, "");
    if (!PHONE_RE.test(f.phone.trim()) || digits.length < 7 || digits.length > 15) e.phone = "Please enter a valid phone number, e.g. 905-761-8554 or +44 20 7946 0000.";
  }
  if (f.contact_method !== "phone" && !EMAIL_RE.test(f.email.trim())) e.email = "Please enter a valid email address.";
  if (f.category === "Commercial" && f.drawings_link.trim() && !/^https?:\/\/\S+\.\S+$/i.test(f.drawings_link.trim())) e.drawings_link = "Please paste a full link starting with https://";
  return e;
};

const Field = ({ id, label, required, error, hint, children }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-semibold text-[#151A20]">
      {label} {required ? <span className="text-[#9B1C1C]" aria-hidden="true">*</span> : <span className="font-normal text-[#4A5866]">(optional)</span>}
    </label>
    {hint && <p id={`${id}-hint`} className="mt-1 text-xs text-[#4A5866]">{hint}</p>}
    <div className="mt-2">{children}</div>
    {error && <p id={`${id}-error`} className="mt-2 text-sm text-[#9B1C1C]" data-testid={`${id}-error`}>{error}</p>}
  </div>
);

const Choice = ({ name, value, current, onChange, label, testid }) => (
  <label className={`choice ${current === value ? "choice-on" : ""}`} data-testid={testid}>
    <input type="radio" name={name} value={value} checked={current === value} onChange={() => onChange(value)} className="sr-only" />
    {label}
  </label>
);

export const EnquiryModal = () => {
  const { open, setOpen, origin } = useEnquiry();
  const { pathname } = useLocation();
  const page = PAGES[pathname] || PAGES["/"];
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [serverError, setServerError] = useState("");
  const openedAt = useRef(0);
  const sending = useRef(false);
  const lastPage = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    openedAt.current = Date.now();
    const fresh = status === "success";
    if (fresh) setStatus("idle");
    setErrors({});
    setForm((f) => {
      const base = fresh ? EMPTY : f;
      const category = page.category || origin.category || (lastPage.current === pathname ? base.category : "");
      const comment = origin.prefill && !base.comment ? origin.prefill : base.comment;
      return { ...base, category, comment };
    });
    lastPage.current = pathname;
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (k) => (v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };
  const onInput = (k) => (e) => set(k)(e.target.value);

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (sending.current) return;
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) {
      const first = formRef.current?.querySelector(`[name="${Object.keys(errs)[0]}"]`);
      if (first) first.focus();
      return;
    }
    sending.current = true;
    setStatus("sending");
    setServerError("");
    try {
      if (!API) throw new Error("The enquiry service is not configured.");
      const body = {
        full_name: form.full_name.trim(),
        contact_method: form.contact_method,
        phone: form.contact_method !== "email" ? form.phone.trim() : null,
        email: form.contact_method !== "phone" ? form.email.trim() : null,
        category: form.category,
        company: form.category === "Commercial" ? form.company.trim() || null : null,
        project_location: form.category === "Commercial" ? form.project_location.trim() || null : null,
        tender_deadline: form.category === "Commercial" ? form.tender_deadline || null : null,
        drawings_link: form.category === "Commercial" ? form.drawings_link.trim() || null : null,
        context: origin.context || null,
        comment: form.comment.trim() || null,
        origin_page: page.label,
        origin_section: origin.section || "Unknown",
        attribution: readAttribution(),
        hp_website: form.hp_website || null,
        elapsed_ms: Date.now() - openedAt.current,
      };
      const res = await fetch(`${API}/enquiries`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data?.detail === "string" ? data.detail : "We couldn't send your enquiry.");
      }
      setStatus("success");
    } catch (err) {
      setServerError(err?.message || "We couldn't send your enquiry.");
      setStatus("error");
    } finally {
      sending.current = false;
    }
  };

  const describe = (id) => (errors[id] ? `${id}-error` : undefined);
  const busy = status === "sending";
  const onCloseAutoFocus = useReturnFocus(open);
  const onOpenChange = (v) => {
    if (busy) return;
    setOpen(v);
    if (!v && status !== "success" && origin.onReturn) setTimeout(origin.onReturn, 160);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="modal-overlay fixed inset-0 z-[80] bg-[#0B1C2D]/70" />
        <Dialog.Content onCloseAutoFocus={onCloseAutoFocus} className="modal-panel fixed z-[90] flex flex-col bg-white text-[#151A20]" data-testid="enquiry-modal" aria-describedby="enquiry-desc">
          <div className="modal-head flex items-start justify-between gap-4 border-b border-[#E2E8EE] bg-white px-6 pb-4 pt-6 md:px-8 md:pt-7">
            <div>
              <Dialog.Title className="text-xl font-bold text-[#102A43] md:text-2xl">Discuss your project</Dialog.Title>
              <Dialog.Description id="enquiry-desc" className="mt-1 text-sm text-[#2B3642]">
                {page.category ? `${page.category} enquiry` : "Tell us a little about what you're planning."}
              </Dialog.Description>
              {origin.context && <p className="mt-2 inline-flex rounded-md border border-[#B8C5D1] bg-[#EEF2F5] px-2.5 py-1 text-xs font-semibold text-[#102A43]" data-testid="enquiry-context">Regarding: {origin.context}</p>}
            </div>
            <Dialog.Close className="btn-glass btn-icon shrink-0" aria-label="Close enquiry form" data-testid="enquiry-close-btn" disabled={busy}>
              <X aria-hidden="true" size={20} />
            </Dialog.Close>
          </div>

          <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-7 md:px-8">
            {status === "success" ? (
              <div className="py-10 text-center" role="status" data-testid="enquiry-success">
                <CheckCircle2 aria-hidden="true" className="mx-auto text-[#102A43]" size={44} />
                <p className="mx-auto mt-5 max-w-sm text-lg font-semibold text-[#151A20]" data-testid="enquiry-success-message">{SUCCESS}</p>
                <Dialog.Close className="btn-glass mt-8" data-testid="enquiry-success-close-btn">Close</Dialog.Close>
              </div>
            ) : (
              <form ref={formRef} onSubmit={submit} noValidate className="grid gap-6" data-testid="enquiry-form">
                <Field id="full_name" label="Full name" required error={errors.full_name}>
                  <input id="full_name" name="full_name" autoComplete="name" className="field" value={form.full_name} onChange={onInput("full_name")} aria-invalid={!!errors.full_name} aria-describedby={describe("full_name")} required data-testid="enquiry-name-input" />
                </Field>

                {page.category ? (
                  <input type="hidden" name="category" value={form.category} data-testid="enquiry-category-fixed" />
                ) : (
                  <fieldset>
                    <legend className="text-sm font-semibold text-[#151A20]">Project type <span className="text-[#9B1C1C]" aria-hidden="true">*</span></legend>
                    <div className="mt-2 grid grid-cols-2 gap-2" role="radiogroup" aria-required="true" aria-describedby={describe("category")}>
                      <Choice name="category" value="Residential" current={form.category} onChange={set("category")} label="Residential" testid="enquiry-category-residential" />
                      <Choice name="category" value="Commercial" current={form.category} onChange={set("category")} label="Commercial" testid="enquiry-category-commercial" />
                    </div>
                    {errors.category && <p id="category-error" className="mt-2 text-sm text-[#9B1C1C]" data-testid="category-error">{errors.category}</p>}
                  </fieldset>
                )}

                <fieldset>
                  <legend className="text-sm font-semibold text-[#151A20]">Preferred contact method</legend>
                  <div className="mt-2 grid grid-cols-3 gap-2" role="radiogroup">
                    <Choice name="contact_method" value="phone" current={form.contact_method} onChange={set("contact_method")} label="Phone" testid="enquiry-method-phone" />
                    <Choice name="contact_method" value="email" current={form.contact_method} onChange={set("contact_method")} label="Email" testid="enquiry-method-email" />
                    <Choice name="contact_method" value="both" current={form.contact_method} onChange={set("contact_method")} label="Both" testid="enquiry-method-both" />
                  </div>
                </fieldset>

                {form.contact_method !== "email" && (
                  <Field id="phone" label="Phone number" required error={errors.phone}>
                    <input id="phone" name="phone" type="tel" inputMode="tel" autoComplete="tel" className="field" value={form.phone} onChange={onInput("phone")} aria-invalid={!!errors.phone} aria-describedby={describe("phone")} required data-testid="enquiry-phone-input" />
                  </Field>
                )}
                {form.contact_method !== "phone" && (
                  <Field id="email" label="Email address" required error={errors.email}>
                    <input id="email" name="email" type="email" inputMode="email" autoComplete="email" className="field" value={form.email} onChange={onInput("email")} aria-invalid={!!errors.email} aria-describedby={describe("email")} required data-testid="enquiry-email-input" />
                  </Field>
                )}
                {form.category === "Commercial" && (
                  <>
                    <Field id="company" label="Company / Organization">
                      <input id="company" name="company" autoComplete="organization" className="field" value={form.company} onChange={onInput("company")} data-testid="enquiry-company-input" />
                    </Field>
                    <div className="grid gap-6 sm:grid-cols-2">
                      <Field id="project_location" label="Project location">
                        <input id="project_location" name="project_location" className="field" value={form.project_location} onChange={onInput("project_location")} data-testid="enquiry-location-input" />
                      </Field>
                      <Field id="tender_deadline" label="Tender deadline">
                        <input id="tender_deadline" name="tender_deadline" type="date" className="field" value={form.tender_deadline} onChange={onInput("tender_deadline")} data-testid="enquiry-deadline-input" />
                      </Field>
                    </div>
                    <Field id="drawings_link" label="Link to drawings / tender documents" error={errors.drawings_link} hint="Paste a shared link (for example Dropbox, Google Drive or a bid portal).">
                      <input id="drawings_link" name="drawings_link" type="url" inputMode="url" className="field" placeholder="https://" value={form.drawings_link} onChange={onInput("drawings_link")} aria-invalid={!!errors.drawings_link} aria-describedby={describe("drawings_link") || "drawings_link-hint"} data-testid="enquiry-drawings-input" />
                    </Field>
                  </>
                )}
                <Field id="comment" label="Comment / context" hint="Project type, location, products of interest, drawings available, timing.">
                  <textarea id="comment" name="comment" rows={5} className="field min-h-[140px] resize-y" value={form.comment} onChange={onInput("comment")} aria-describedby="comment-hint" data-testid="enquiry-comment-input" />
                </Field>

                <div className="hp-field" aria-hidden="true">
                  <label htmlFor="hp_website">Website</label>
                  <input id="hp_website" name="hp_website" tabIndex={-1} autoComplete="off" value={form.hp_website} onChange={onInput("hp_website")} />
                </div>

                {status === "error" && (
                  <div role="alert" className="flex gap-3 border border-[#E5B4B4] bg-[#FDF3F3] p-4 text-sm text-[#7A1A1A]" data-testid="enquiry-error">
                    <AlertCircle aria-hidden="true" size={18} className="mt-0.5 shrink-0" />
                    <div>
                      <p className="font-semibold">Your enquiry was not sent.</p>
                      <p className="mt-1">{serverError.replace(/\.?$/, ".")} Your details are still here. Please try again, or call {company.tel}.</p>
                      <button type="button" onClick={submit} className="mt-3 font-semibold underline underline-offset-4" data-testid="enquiry-retry-btn">Try again</button>
                    </div>
                  </div>
                )}

                <p className="text-xs leading-relaxed text-[#3E4C59]">
                  Privacy: we use your details only to respond to this enquiry.
                </p>
                <button type="submit" className="btn-glass btn-solid w-full justify-center" disabled={busy} aria-busy={busy} data-testid="enquiry-submit-btn">
                  {busy ? (<><Loader2 aria-hidden="true" size={18} className="animate-spin" /> Sending…</>) : "Send enquiry"}
                </button>
              </form>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
